import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SettingsModalBoldText } from '../../settings/SettingsModals';
import { closeAddUserTypeModal } from '../ManageUserComponents';
import {
  ModalButtons,
  ModalHeader,
  StyledModal,
  StyledModalBody
} from '../../../components/Modals';
import 'antd/dist/antd.css';
import { openModal } from '../../../helper/modals';
import { updateUserTypeData } from '../../../redux/actions/addUserType';
import { Form, FormControl, FormField, FormItem, FormMessage } from '../../../components/ui/Form';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { GET_ALL_BUILDINGS } from '../../../redux/constants';
import { requestPending } from '../../../utils/status';
import { LoadingSpinner } from '../../payments/PaymentComponents';
import {
  IconOption,
  StyledSelect,
  inputSelectCustomStyles
} from '../../../components/CustomSearchBar';

const schema = Yup.object().shape({
  assignedBuildings: Yup.array().of(Yup.number())
});

const AddNewUserTypeModalStepBuildingManagerAccess = () => {
  const { t } = useTranslation();
  const modal = useSelector((state) => state.headerModal.modal);
  const addUserType = useSelector((state) => state.addUserType);
  const dispatch = useDispatch();
  const allBuildings = useSelector((state) => state.building.allBuildings);
  const allBuildingsStatus = useSelector((state) => state.building.allBuildingsStatus);

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    defaultValues: {
      assignedBuildings: addUserType.assignedBuildings || []
    }
  });

  const onSubmit = (values) => {
    const assignedBuildings = values.assignedBuildings.filter(
      (buildingId) => buildingId !== addUserType.buildings.buildingId
    );
    dispatch(
      updateUserTypeData({
        assignedBuildings: assignedBuildings
      })
    );
    openModal('AddNewUserTypeModalStep3');
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <StyledModal
          show={modal === 'AddNewUserTypeModalStepBuildingManagerAccess'}
          closeOnBackdrop={false}
          onClose={() => closeAddUserTypeModal(addUserType.editingID)}>
          <ModalHeader
            title={t('Manager Access')}
            onCloseCallback={() => closeAddUserTypeModal(addUserType.editingID)}
          />

          <StyledModalBody gap="0px">
            <p>{t('Please select the buildings that the user will have access to.')}</p>
            <FormField
              control={form.control}
              name="assignedBuildings"
              render={({ field }) => {
                const options = allBuildings
                  ?.filter(
                    (building) =>
                      building.id !== addUserType.buildings.buildingId &&
                      building.org_id === addUserType.organization.organizationId
                  )
                  .map((building) => ({
                    label: building.name,
                    value: building.id
                  }));
                const isLoading = allBuildingsStatus === requestPending(GET_ALL_BUILDINGS);
                return (
                  <FormItem alt>
                    <SettingsModalBoldText>{t('Select buildings')}</SettingsModalBoldText>
                    <FormControl>
                      <StyledSelect
                        {...field}
                        getPopupContainer={() => document.body}
                        styles={inputSelectCustomStyles({ minHeight: '56px', height: 'auto' })}
                        menuPortalTarget={document.body}
                        className="react-select-container"
                        classNamePrefix="react-select"
                        components={{
                          IndicatorSeparator: () => null,
                          Option: IconOption,
                          LoadingIndicator: () => <LoadingSpinner />
                        }}
                        closeMenuOnSelect={false}
                        isSearchable={true}
                        placeholder={isLoading ? t('Loading...') : t('Please select')}
                        options={options}
                        isMulti={true}
                        isLoading={isLoading}
                        isDisabled={isLoading}
                        onChange={(selected) => {
                          // Handle multi vs single
                          const values = Array.isArray(selected)
                            ? selected.map((opt) => opt.value)
                            : selected?.value;

                          field.onChange(values);
                        }}
                        value={
                          field.value
                            ? [...options.filter((opt) => field.value.includes(opt.value))]
                            : []
                        }></StyledSelect>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </StyledModalBody>
          <ModalButtons
            leftButtonText={t('Back')}
            leftButtonClick={() => openModal('AddNewUserTypeModalStep2')}
            rightButtonText={t('Next')}
          />
        </StyledModal>
      </form>
    </Form>
  );
};

export default AddNewUserTypeModalStepBuildingManagerAccess;
